<template>
  <div class="d-flex-column justify-start align-start gap-5">
    <h4 class="t-white font-family-1 title-h5 f-w-600">
      {{ $t("new_text.select_category") }}
    </h4>

    <div class="mobile_camo_picker_container b-radius-12">
      <button
        class="mobile_camo_selector b-radius-6 space-between"
        :class="[isOpen && 'opened']"
        @click.stop="toggleOpen"
        v-click-outside="closeOutside"
      >
        <p class="t-white medium-l font-family-2">
          {{ $t(`${currentGameSlug}.weapons.${selectedCategory}`) }}
        </p>
        <ChevDown />

        <Transition name="slide-fade-top">
          <div class="camo_list_container b-radius-6" v-if="isOpen">
            <button
              class="camo_option t-white medium-l font-family-2"
              v-for="packOption in weaponsCategory"
              :key="packOption"
              :class="[
                selectedCategory === packOption && 'selected',
                hasClassSelected(packOption) && 'red_dot',
              ]"
              @click="selectCategory(packOption)"
            >
              {{ $t(`${currentGameSlug}.weapons.${packOption}`) }}
            </button>
          </div>
        </Transition>
      </button>
      <div
        class="d-flex-column justify-start align-start gap-10 armor_type_picker"
      >
        <label class="medium-m font-family-2 t-grey caps">{{
          $t("new_text.select_weapon")
        }}</label>
        <div class="d-flex-row justify-start flex-wrap">
          <button
            v-for="option in weapons"
            class="d-flex-row space-between gap-5 secondary option selected medium-l font-family-2 b-radius-6"
            style="padding: 6px 12px"
            :class="[weaponExists(option.title) && 'active']"
            @click="selectWeapon(option)"
          >
            {{ option.title }}
            <p class="regular-m t-grey font-family-2 t-blue">
              {{ getUserCurrencyCode }}{{ price(option.price) }}
            </p>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { calculateCurrency } from "../../../../calculate/calculatePrice.js";
import ChevDown from "~/components/icons/ChevDown.vue";
const { currentGameSlug } = useGameComposable();
const gameStore = useGameStore();
const { getUserCurrencyCode } = useUserStore();
const checkoutStore = useCheckoutStore();

const isOpen = ref(false);
const toggleOpen = () => {
  isOpen.value = !isOpen.value;
};
const closeOutside = () => {
  isOpen.value = false;
};

const weaponsCategory = computed(() => {
  const packPrices =
    gameStore.getGamePack?.prices?.filter((p) => p.description !== "bundle") ||
    [];
  if (packPrices.length > 0) {
    const uniqueDescriptions = [
      ...new Set(packPrices.map((item) => item.description)),
    ];
    return uniqueDescriptions;
  }
  return [];
});
const selectedCategory = ref("assault_rifle");

const selectCategory = (cat) => {
  selectedCategory.value = cat;
};

const weapons = computed(() => {
  const packPrices = gameStore.getGamePack?.prices || [];

  const wps = packPrices.filter(
    (item) => item.description === selectedCategory.value
  );
  if (wps.length > 0) {
    return wps;
  }
  return [];
});

const selectedOption = computed(() => {
  const options = gameStore.getGamePack.pack_options || [];
  const op = options.find((op) => op.id === checkoutStore.getCamoCategory);

  return op;
});

const weaponExists = (option) => {
  const weapon = {
    category: selectedOption.value,
    weapon: option,
  };

  const selectedWeapons = checkoutStore.getCamoWeapon;
  const isSelected = selectedWeapons.find(
    (wp) =>
      wp.category === selectedOption.value?.const_title &&
      wp.weapon === weapon.weapon
  );
  return Boolean(isSelected);
};

const selectWeapon = (option) => {
  const catPrice = selectedOption.value?.price || 0;

  const weapon = {
    category: selectedOption.value?.const_title,
    weapon: option.title,
    price: option.price,
    catPrice: catPrice,
    timing: option.timing,
    id: option.id,
    category_id: selectedOption.value?.id,
    class: option.description,
  };

  const selectedWeapons = [...checkoutStore.getCamoWeapon];

  if (weaponExists(option.title)) {
    const updatedWeapons = selectedWeapons.filter(
      (wp) =>
        wp.category !== selectedOption.value?.const_title ||
        wp.weapon !== weapon.weapon
    );
    checkoutStore.setCamoWeapon(updatedWeapons);
  } else {
    checkoutStore.setCamoWeapon([...selectedWeapons, weapon]);
  }
};

const hasClassSelected = (classtitle) => {
  const selectedCamo = selectedOption.value?.id;
  if (!selectedCamo) return false;
  const selectedWeapon = checkoutStore.getCamoWeapon;
  if (selectedWeapon?.length <= 0) return false;
  const hasWeapon = selectedWeapon?.some(
    (wp) => wp.class === classtitle && wp.category_id === selectedCamo
  );
  return hasWeapon;
};

const price = (cp) => {
  const catPrice = selectedOption.value?.price || 0;
  const pp = cp + (catPrice * cp) / 100;

  return parseFloat(calculateCurrency(pp)).toFixed(2);
};
</script>

<style lang="scss">
.mobile_camo_picker_container {
  width: 100%;
  padding: 12px;
  border: 1px solid var(--stroke-dark);
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  flex-direction: column;
  gap: 10px;
  .mobile_camo_selector {
    width: 100%;
    padding: 9px 12px;
    border: 1px solid var(--blue);
    background-color: var(--dark-blue);
    position: relative;
    svg {
      transition: all 0.2s ease;
    }
    &.opened {
      svg {
        transform: rotate(180deg);
      }
    }
    .camo_list_container {
      position: absolute;
      border: 1px solid var(--stroke-dark);
      top: 110%;
      left: 0;
      width: 100%;
      height: max-content;
      max-height: 350px;
      padding: 9px 12px;
      z-index: 9;
      display: flex;
      justify-content: flex-start;
      align-items: flex-start;
      gap: 6px;
      flex-direction: column;
      background-color: var(--bg-main);
      overflow: auto;
      .camo_option {
        width: 100%;
        background-color: var(--bg-medium);
        padding: 9px 12px;
        border-radius: 9px;
        border: 1px solid transparent;
        position: relative;
        &.selected {
          border: 1px solid var(--blue);
          background-color: var(--dark-blue);
        }
        &.red_dot {
          &::after {
            content: "";
            position: absolute;
            top: -10%;
            right: 0;
            width: 10px;
            height: 10px;
            border-radius: 50%;
            z-index: 10;
            background-color: var(--call-of-duty);
            border: 1px solid var(--bg-main);
          }
        }
      }
    }
  }
}
</style>
